'use client';
import { useModalContext } from '#pplx/frontend/aether/modals/context/useModalContext.js';
import { clearPostCheckoutTierOverride, resolvePostCheckoutTier, } from '#pplx/frontend/apps/ask/billing/checkout/postCheckoutTierOverride.js';
import { setTasksSettingsSeenCookie } from '#pplx/frontend/apps/ask/browser-state/cookieManager.js';
import { useBrandedVisitorGate } from '#pplx/frontend/apps/ask/hooks/branded-visitor-gate-hooks/useBrandedVisitorGate.js';
import { useHandleClickPricing } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/useHandleClickPricing.js';
import { usePaywallUpsell } from '#pplx/frontend/apps/ask/hooks/pricing-hooks/usePaywallUpsell.js';
import { useLaunchEnterpriseComputerOnboardingV2 } from '#pplx/frontend/apps/ask/onboarding-computer/v2/useLaunchEnterpriseComputerOnboardingV2.js';
import { isSubscriptionTier, isTierUpgrade, } from '#pplx/frontend/apps/ask/organization/types/enterpriseTiers.js';
import { ENTERPRISE_UPSELL_MODAL_PARAM, ORG_DELETED_PARAM, } from '#pplx/frontend/apps/ask/organization/utils/enterpriseOnboarding.js';
import { CREDIT_UPSELL_RETURN_PARAM, getCreditUpsellReturnPath, } from '#pplx/frontend/apps/ask/permalink-slug/creditUpsellReturn.js';
import { updateSearchParams } from '#pplx/frontend/apps/ask/routing/historySearchParams.js';
import { routes } from '#pplx/frontend/apps/ask/routing/spaRoutes.js';
import { SHEER_ID_ORIGIN, SHEER_ID_URL_PARAM, } from '#pplx/frontend/apps/ask/students/sheerid-constants.js';
import { useIsEnterpriseUser } from '#pplx/frontend/auth/useIsEnterpriseUser.js';
import { useIsSubscribed } from '#pplx/frontend/auth/useIsSubscribed.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { ModalOrigin, parseModalOrigin } from '#pplx/frontend/components/types/types.js';
import { Dynamic } from '#pplx/frontend/dynamic/Dynamic.js';
import { FeatureFlagsContext } from '#pplx/frontend/experimentation/context.js';
import { waitForFlagValue } from '#pplx/frontend/experimentation/hooks.js';
import { logger } from '#pplx/frontend/logger/browser.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useSearchParams } from '#pplx/frontend/router/useSearchParams.js';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
// Supersede pending work across route mounts without cancelling ordinary navigation.
const pendingCheckouts = new WeakMap();
const SubSuccessModalDynamic = Dynamic(async () => {
    const { SubSuccessModal } = await import('#pplx/frontend/apps/ask/subscription-ui/SubSuccessModal.js');
    return { default: SubSuccessModal };
});
const ConsumerUpgradeWelcomeModalDynamic = Dynamic(async () => {
    const { ConsumerUpgradeWelcomeModal } = await import('#pplx/frontend/apps/ask/subscription-ui/ConsumerUpgradeWelcomeModal.js');
    return { default: ConsumerUpgradeWelcomeModal };
});
const SheerIdRedirectModalDynamic = Dynamic(async () => import('#pplx/frontend/apps/ask/referrals/SheerIdRedirectModal.js'));
const SubscriptionPromotionModalDynamic = Dynamic(async () => {
    const { SubscriptionPromotionModal } = await import('#pplx/frontend/apps/ask/subscription/components/SubscriptionPromotionModal.js');
    return { default: SubscriptionPromotionModal };
});
const CreditUpsellReturnModalDynamic = Dynamic(async () => {
    const { CreditUpsellReturnModal } = await import('#pplx/frontend/apps/ask/subscription/components/CreditUpsellReturnModal.js');
    return { default: CreditUpsellReturnModal };
});
const ConsumerComputerOnboardingPostCheckoutModalDynamic = Dynamic(async () => {
    const { ConsumerComputerOnboardingPostCheckoutModal } = await import('#pplx/frontend/apps/ask/onboarding-computer/v2/ConsumerComputerOnboardingPostCheckoutModal.js');
    return { default: ConsumerComputerOnboardingPostCheckoutModal };
});
const ConsumerComputerOnboardingDirectLinkModalDynamic = Dynamic(async () => {
    const { ConsumerComputerOnboardingDirectLinkModal } = await import('#pplx/frontend/apps/ask/onboarding-computer/v2/ConsumerComputerOnboardingPostCheckoutModal.js');
    return { default: ConsumerComputerOnboardingDirectLinkModal };
});
const getSubscriptionTier = (subscriptionTierParam) => {
    if (subscriptionTierParam === 'pro' || subscriptionTierParam === 'max') {
        return subscriptionTierParam;
    }
    return undefined;
};
function resolveUpsellOrigin(src) {
    return parseModalOrigin(src) ?? ModalOrigin.COMET_SIDECAR_RECURRING_NUDGE;
}
// Opens the consumer plan-selection paywall right after a self-serve org
// deletion (`?org-deleted=1`). The subscription check must happen here, after
// the post-delete reload: while the user was still an org member the session
// reported their org grant (subscription_source='enterprise') and hid any
// underlying individual subscription. By now the session reflects their
// personal state, so users who kept an active individual subscription are not
// shown the paywall.
const useOrgDeletedUpsellHandler = () => {
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const { hasActiveSubscription, isEnterprise } = useIsSubscribed();
    const openPaywallUpsell = usePaywallUpsell();
    const searchParams = useSearchParams();
    const orgDeletedParam = searchParams?.get(ORG_DELETED_PARAM);
    const handledRef = useRef(false);
    // Only an active *individual* subscription suppresses the paywall. The org
    // was just deleted, so an enterprise-sourced "active" status can only be a
    // stale session that hasn't picked up the deletion yet — treat it as
    // unsubscribed rather than letting it hide the upsell.
    const hasIndividualSubscription = hasActiveSubscription && !isEnterprise;
    useEffect(() => {
        if (orgDeletedParam == null || handledRef.current)
            return;
        // Wait until auth state is resolved so the subscription check is accurate.
        if (isUserLoggedInLoading)
            return;
        handledRef.current = true;
        if (isUserLoggedIn && !hasIndividualSubscription) {
            openPaywallUpsell({ origin: ModalOrigin.ORG_DELETED_UPSELL });
        }
        else {
            logger.warn('Skipping post-org-deletion paywall', {
                isUserLoggedIn,
                hasActiveSubscription,
                isEnterprise,
            });
        }
        updateSearchParams({ [ORG_DELETED_PARAM]: undefined });
    }, [
        orgDeletedParam,
        isUserLoggedIn,
        isUserLoggedInLoading,
        hasIndividualSubscription,
        hasActiveSubscription,
        isEnterprise,
        openPaywallUpsell,
    ]);
};
const useEnterpriseUpsellModalHandler = () => {
    const router = useRouter();
    const { $t } = useIntl();
    const isLoggedIn = useIsUserLoggedIn();
    const { isEnterprise, subscriptionTier } = useIsSubscribed();
    const openPaywallUpsell = usePaywallUpsell();
    const searchParams = useSearchParams();
    const openPaywallModalParam = searchParams?.get(ENTERPRISE_UPSELL_MODAL_PARAM);
    const isUpsellTriggered = openPaywallModalParam !== null;
    const isMaxUpsell = isSubscriptionTier(openPaywallModalParam) &&
        openPaywallModalParam === 'max' &&
        (!subscriptionTier || isTierUpgrade(subscriptionTier, 'max'));
    const removeOpenPaywallModalParam = useCallback(() => {
        const url = new URL(window.location.href);
        url.searchParams.delete(ENTERPRISE_UPSELL_MODAL_PARAM);
        router.replace(url.toString());
    }, [router]);
    const openPricingModal = useCallback((pitchMessage) => {
        openPaywallUpsell({
            origin: ModalOrigin.ENTERPRISE_UPSELL_URL_HANDLER,
            defaultSegment: 'business',
            segments: ['business'],
            pitchMessage,
        });
        removeOpenPaywallModalParam();
    }, [openPaywallUpsell, removeOpenPaywallModalParam]);
    const redirectToOrgCreate = useCallback(() => {
        router.push('/onboarding/org/create');
    }, [router]);
    useEffect(() => {
        if (!isUpsellTriggered) {
            return;
        }
        if (!isLoggedIn) {
            redirectToOrgCreate();
            return;
        }
        if (isMaxUpsell) {
            const pitch = {
                title: $t({
                    defaultMessage: 'Introducing Perplexity Max',
                    description: 'Title for the Perplexity Max paywall upsell triggered by enterprise URL parameter',
                    id: 'f9f9bbccf1',
                }),
                description: $t({
                    defaultMessage: 'Maximize your curiosity with enhanced access to Perplexity file and app creation',
                    description: 'Description for the Perplexity Max paywall upsell triggered by enterprise URL parameter',
                    id: '549d404ee3',
                }),
            };
            openPricingModal(pitch);
            return;
        }
        if (!isEnterprise) {
            openPricingModal();
            return;
        }
        removeOpenPaywallModalParam();
    }, [
        openPricingModal,
        isUpsellTriggered,
        isEnterprise,
        isLoggedIn,
        redirectToOrgCreate,
        isMaxUpsell,
        $t,
        removeOpenPaywallModalParam,
    ]);
};
export const useUrlModalHandler = ({ confirmationRedirectsToHomepage, disablePostCheckoutOnboardingModal = false, } = {}) => {
    const searchParams = useSearchParams();
    const router = useRouter();
    const stripeCheckoutParam = searchParams?.get('stripe_checkout');
    const creditUpsellReturnParam = searchParams?.get(CREDIT_UPSELL_RETURN_PARAM);
    const upsellParam = searchParams?.get('upsell');
    const upsellSrcParam = searchParams?.get('src');
    const promoParam = searchParams?.get('px_promo');
    const legacyTrialParam = searchParams?.get('trial');
    const flowTypeParam = searchParams?.get('flow_type');
    const upgradeParam = searchParams?.get('upgrade_max');
    const subscriptionTierParam = searchParams?.get('subscription_tier');
    const computerOnboardingParam = searchParams?.get('computer_onboarding');
    const isEnterpriseParam = searchParams?.get('is_enterprise') === 'true';
    const isEnterpriseFromSession = useIsEnterpriseUser();
    const isEnterpriseCheckout = isEnterpriseParam || isEnterpriseFromSession;
    const launchEnterpriseComputerOnboardingV2 = useLaunchEnterpriseComputerOnboardingV2();
    const initialEnterpriseComputerOnboardingTierRef = useRef(isEnterpriseParam ? getSubscriptionTier(computerOnboardingParam) : undefined);
    const openPaywallUpsell = usePaywallUpsell();
    const { openVisitorLoginUpsell } = useBrandedVisitorGate({ enabled: true });
    const isUserLoggedIn = useIsUserLoggedIn();
    const isUserLoggedInLoading = useIsUserLoggedInLoading();
    const creditUpsellReturnPath = creditUpsellReturnParam === '1' && subscriptionTierParam === 'max'
        ? getCreditUpsellReturnPath(window.location.pathname)
        : undefined;
    const flags = useContext(FeatureFlagsContext);
    // Tracks which `?upsell=` value we've already surfaced so the modal doesn't
    // reopen on the second strict-mode pass or on later re-renders.
    const handledUpsellParamRef = useRef(null);
    const { handleClickPersonal } = useHandleClickPricing({
        reason: 'subscription-promo-description',
    });
    const { openModal } = useModalContext();
    const { $t } = useIntl();
    const hasOpenedPostCheckoutModalRef = useRef(false);
    const handledComputerOnboardingParamRef = useRef(null);
    const sheerIdRedirectParam = searchParams?.get(SHEER_ID_URL_PARAM);
    useEnterpriseUpsellModalHandler();
    useOrgDeletedUpsellHandler();
    // Memoize the modal opening functions to prevent unnecessary re-renders
    const openSubSuccessModal = useCallback((subscriptionTierOverride, redirectsHome) => {
        const flowType = flowTypeParam ?? 'unknown';
        const subscriptionTier = subscriptionTierOverride ?? getSubscriptionTier(subscriptionTierParam);
        openModal(SubSuccessModalDynamic, {
            flowType,
            subscriptionTier,
            isEnterprise: isEnterpriseParam,
            confirmationRedirectsToHomepage: redirectsHome ?? confirmationRedirectsToHomepage,
        }, { replace: true });
    }, [
        flowTypeParam,
        openModal,
        subscriptionTierParam,
        isEnterpriseParam,
        confirmationRedirectsToHomepage,
    ]);
    const openConsumerComputerOnboardingPostCheckoutModal = useCallback((subscriptionTier, redirectsHome) => {
        openModal(ConsumerComputerOnboardingPostCheckoutModalDynamic, {
            flowType: flowTypeParam ?? 'unknown',
            subscriptionTier,
            confirmationRedirectsToHomepage: redirectsHome ?? confirmationRedirectsToHomepage,
        }, { replace: true });
    }, [confirmationRedirectsToHomepage, flowTypeParam, openModal]);
    const openConsumerComputerOnboardingDirectLinkModal = useCallback((subscriptionTier) => {
        openModal(ConsumerComputerOnboardingDirectLinkModalDynamic, {
            subscriptionTier,
        }, { replace: true });
    }, [openModal]);
    const completeResumedEnterpriseComputerOnboarding = useCallback((closeCurrentModal) => {
        closeCurrentModal();
        updateSearchParams({
            computer_onboarding: undefined,
            computer_onboarding_origin: undefined,
            is_enterprise: undefined,
        });
        router.push(routes['/computer'].href());
    }, [router]);
    const openConsumerPostCheckoutModal = useCallback(async (subscriptionTier) => {
        const pathname = window.location.pathname;
        pendingCheckouts.get(flags)?.abort();
        const pending = new AbortController();
        pendingCheckouts.set(flags, pending);
        try {
            let complete = true;
            try {
                const { isComputerOnboardingV2CompleteForTier } = await import('#pplx/frontend/apps/ask/onboarding-computer/v2/postCheckoutOnboarding.js');
                complete = await isComputerOnboardingV2CompleteForTier(subscriptionTier, 'computer-onboarding-v2-url-modal-handler');
            }
            catch (error) {
                logger.error('Failed to launch consumer computer onboarding v2', error);
            }
            if (pending.signal.aborted)
                return;
            // Choose the UI in the existing async continuation, not route-local React state.
            let welcomeFlow = 'disabled';
            try {
                welcomeFlow = await waitForFlagValue(flags, { flag: 'consumer-upgrade-welcome', defaultValue: 'disabled' }, { timeoutMs: 10_000, signal: pending.signal });
            }
            catch (error) {
                logger.error('Failed to resolve consumer upgrade welcome', error);
            }
            if (pending.signal.aborted)
                return;
            const redirectsHome = pathname === window.location.pathname ? confirmationRedirectsToHomepage : false;
            if (welcomeFlow === 'fullscreen_modal') {
                openModal(ConsumerUpgradeWelcomeModalDynamic, {
                    confirmationRedirectsToHomepage: redirectsHome ?? true,
                    subscriptionTier,
                    flowType: flowTypeParam ?? 'unknown',
                }, { replace: true });
            }
            else if (complete) {
                openSubSuccessModal(subscriptionTier, redirectsHome);
            }
            else {
                openConsumerComputerOnboardingPostCheckoutModal(subscriptionTier, redirectsHome);
            }
        }
        finally {
            if (pendingCheckouts.get(flags) === pending)
                pendingCheckouts.delete(flags);
        }
    }, [
        flags,
        openModal,
        confirmationRedirectsToHomepage,
        flowTypeParam,
        openSubSuccessModal,
        openConsumerComputerOnboardingPostCheckoutModal,
    ]);
    useEffect(() => {
        if (stripeCheckoutParam === 'success' || upgradeParam === 'success') {
            handledComputerOnboardingParamRef.current = null;
            return;
        }
        if (isUserLoggedInLoading) {
            return;
        }
        const computerOnboardingTier = getSubscriptionTier(computerOnboardingParam);
        if (!computerOnboardingTier) {
            handledComputerOnboardingParamRef.current = null;
            return;
        }
        const isEnterpriseContinuation = isEnterpriseParam &&
            initialEnterpriseComputerOnboardingTierRef.current === computerOnboardingTier;
        const computerOnboardingKey = `${isEnterpriseContinuation ? 'enterprise' : 'direct'}:${computerOnboardingTier}`;
        if (handledComputerOnboardingParamRef.current === computerOnboardingKey) {
            return;
        }
        if (isEnterpriseContinuation) {
            if (!isEnterpriseFromSession) {
                return;
            }
            handledComputerOnboardingParamRef.current = computerOnboardingKey;
            void launchEnterpriseComputerOnboardingV2({
                completeEnterpriseOnboarding: completeResumedEnterpriseComputerOnboarding,
                metadata: {
                    entrypoint: 'enterprise_admin_creation',
                    subscriptionTier: computerOnboardingTier,
                },
            }).then((didLaunch) => {
                if (!didLaunch) {
                    handledComputerOnboardingParamRef.current = null;
                }
            });
            return;
        }
        if (isEnterpriseFromSession) {
            handledComputerOnboardingParamRef.current = computerOnboardingKey;
            return;
        }
        handledComputerOnboardingParamRef.current = computerOnboardingKey;
        openConsumerComputerOnboardingDirectLinkModal(computerOnboardingTier);
    }, [
        completeResumedEnterpriseComputerOnboarding,
        computerOnboardingParam,
        isEnterpriseFromSession,
        isUserLoggedInLoading,
        isEnterpriseParam,
        launchEnterpriseComputerOnboardingV2,
        openConsumerComputerOnboardingDirectLinkModal,
        stripeCheckoutParam,
        upgradeParam,
    ]);
    const openTasksUpsellModal = useCallback(() => {
        setTasksSettingsSeenCookie(true);
        openPaywallUpsell({
            origin: ModalOrigin.TASKS_PAGE,
            pitchMessage: {
                title: $t({
                    defaultMessage: 'Want to use Perplexity Tasks?',
                    description: 'Title for the tasks paywall upsell modal',
                    id: '140f15a986',
                }),
                description: $t({
                    defaultMessage: 'Upgrade to Pro to create and manage tasks.',
                    description: 'Description for the tasks paywall upsell modal',
                    id: 'fa6435fb5e',
                }),
            },
        });
    }, [$t, openPaywallUpsell]);
    const openSubscriptionPromotionModal = useCallback(() => {
        openModal(SubscriptionPromotionModalDynamic, {
            onPaymentClick: (plan) => {
                handleClickPersonal({
                    billingPeriod: plan,
                    subscriptionTier: 'pro',
                    origin: ModalOrigin.SUBSCRIPTION_PROMO_DESCRIPTION,
                });
            },
        }, { replace: true });
    }, [handleClickPersonal, openModal]);
    const openModelsUpsellModal = useCallback(() => {
        openPaywallUpsell({
            origin: ModalOrigin.MODEL_SELECTOR,
            pitchMessage: {
                title: $t({
                    defaultMessage: 'Upgrade to choose a model',
                    description: 'Title for the models paywall upsell modal',
                    id: '1961a187ab',
                }),
                description: $t({
                    defaultMessage: 'With Perplexity Pro, you get access to all the latest AI models in one subscription',
                    description: 'Description for the models paywall upsell modal',
                    id: '0aaafeabd1',
                }),
            },
        });
    }, [$t, openPaywallUpsell]);
    const openSourcesUpsellModal = useCallback(() => {
        openPaywallUpsell({
            origin: ModalOrigin.SOURCES_VIEW_MORE,
            pitchMessage: {
                title: $t({
                    defaultMessage: 'Upgrade to connect more sources',
                    description: 'Title for the sources paywall upsell modal',
                    id: '86e5397fff',
                }),
                description: $t({
                    defaultMessage: 'With Perplexity Pro, connect more sources to enhance your searches',
                    description: 'Description for the sources paywall upsell modal',
                    id: '6a839eca3f',
                }),
            },
        });
    }, [$t, openPaywallUpsell]);
    const openSheerIdRedirectModal = useCallback(() => {
        if (sheerIdRedirectParam) {
            try {
                const url = new URL(sheerIdRedirectParam.toString());
                if (url.hostname === new URL(SHEER_ID_ORIGIN).hostname &&
                    url.protocol === new URL(SHEER_ID_ORIGIN).protocol) {
                    const handlers = openModal(SheerIdRedirectModalDynamic, {
                        onContinue: () => {
                            handlers.closeModal();
                            window.open(url.toString(), '_blank', 'noopener,noreferrer');
                        },
                    }, { replace: true });
                }
            }
            catch {
                logger.warn('Invalid SheerId redirect URL', {
                    sheerIdRedirectParam,
                });
            }
        }
    }, [openModal, sheerIdRedirectParam]);
    useEffect(() => {
        // Post-checkout modal — opens after a successful checkout or upgrade.
        // The occupation form used to chain in front of this; it now lives in
        // the new-user onboarding flow as a dedicated step.
        if (disablePostCheckoutOnboardingModal) {
            return;
        }
        if (stripeCheckoutParam !== 'success' && upgradeParam !== 'success') {
            hasOpenedPostCheckoutModalRef.current = false;
            return;
        }
        if (hasOpenedPostCheckoutModalRef.current) {
            return;
        }
        if (isUserLoggedInLoading) {
            return;
        }
        hasOpenedPostCheckoutModalRef.current = true;
        const upgradeTier = resolvePostCheckoutTier(subscriptionTierParam);
        if (!isEnterpriseCheckout && creditUpsellReturnPath) {
            clearPostCheckoutTierOverride();
            openModal(CreditUpsellReturnModalDynamic, { creditUpsellReturnPath, flowType: flowTypeParam ?? 'unknown' }, { replace: true });
            return;
        }
        if (!isEnterpriseCheckout) {
            void openConsumerPostCheckoutModal(upgradeTier ?? 'pro');
            return;
        }
        openSubSuccessModal();
    }, [
        creditUpsellReturnPath,
        subscriptionTierParam,
        flowTypeParam,
        openModal,
        disablePostCheckoutOnboardingModal,
        isEnterpriseCheckout,
        isUserLoggedInLoading,
        openConsumerPostCheckoutModal,
        openSubSuccessModal,
        stripeCheckoutParam,
        upgradeParam,
    ]);
    useEffect(() => {
        // Tasks pro upsell modal
        if (upsellParam === 'tasks') {
            openTasksUpsellModal();
        }
        // Models pro upsell modal
        if (upsellParam === 'models') {
            openModelsUpsellModal();
        }
        // Sources pro upsell modal
        if (upsellParam === 'sources') {
            openSourcesUpsellModal();
        }
        // Subscription promotion modal (supports new ?px_promo=7dpro and legacy ?trial param)
        if (promoParam === '7dpro' || legacyTrialParam !== null) {
            openSubscriptionPromotionModal();
        }
        // SheerId redirect modal
        if (sheerIdRedirectParam) {
            openSheerIdRedirectModal();
        }
        return undefined;
    }, [
        openModelsUpsellModal,
        openSheerIdRedirectModal,
        openSourcesUpsellModal,
        openSubscriptionPromotionModal,
        openTasksUpsellModal,
        sheerIdRedirectParam,
        promoParam,
        legacyTrialParam,
        upsellParam,
    ]);
    // Surfaces that cannot render modals directly (e.g. the Comet sidecar) open
    // a new tab into the main app with `?upsell=upgrade` or `?upsell=login` and
    // rely on this hook to surface the corresponding modal. Both target hooks
    // silently no-op while the session is still loading, so we wait until the
    // auth state is resolved and only strip the URL after a successful open.
    useEffect(() => {
        if (upsellParam !== 'upgrade' && upsellParam !== 'login') {
            handledUpsellParamRef.current = null;
            return;
        }
        if (handledUpsellParamRef.current === upsellParam)
            return;
        if (isUserLoggedInLoading)
            return;
        const resolvedOrigin = resolveUpsellOrigin(upsellSrcParam);
        if (upsellParam === 'upgrade' && !isUserLoggedIn) {
            openVisitorLoginUpsell({ origin: resolvedOrigin });
        }
        else if (upsellParam === 'upgrade') {
            openPaywallUpsell({ origin: resolvedOrigin });
        }
        else {
            openVisitorLoginUpsell({ origin: resolvedOrigin });
        }
        handledUpsellParamRef.current = upsellParam;
        const url = new URL(window.location.href);
        url.searchParams.delete('upsell');
        url.searchParams.delete('src');
        router.replace(url.toString());
    }, [
        upsellParam,
        upsellSrcParam,
        isUserLoggedIn,
        isUserLoggedInLoading,
        openPaywallUpsell,
        openVisitorLoginUpsell,
        router,
    ]);
};
//# sourceMappingURL=useUrlModalHandler.js.map